import { css } from "@emotion/css";
import { Button } from "@octopusdeploy/design-system-components";
import { GithubIcon } from "@octopusdeploy/design-system-icons";
import { space, text, themeTokens, borderRadius, borderWidth } from "@octopusdeploy/design-system-tokens";
import { useMutation } from "@octopusdeploy/octopus-react-client";
import type { GitHubAppAuthorizationStatus, GitHubAppAuthorizationStatusWithUserDetails } from "@octopusdeploy/octopus-server-client";
import React from "react";
import type { MenuItem } from "~/components/OverflowMenu/OverflowMenu";
import { OverflowMenu, OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import { GitHubUserAvatar } from "./GitHubUserAvatar";
interface GitHubUserCardProps {
    authorizationStatus: GitHubAppAuthorizationStatus | GitHubAppAuthorizationStatusWithUserDetails;
    onUserUpdated: () => void;
}
export function GitHubUserCard({ authorizationStatus, onUserUpdated }: GitHubUserCardProps) {
    const { execute: refreshUser } = useMutation({
        name: "Refresh GitHub user",
        action: async (repository) => await repository.GitHubApp.refreshUser(),
        afterComplete: async () => onUserUpdated(),
    });
    const { execute: reauthorizeUser } = useMutation({
        name: "Reauthorize GitHub user",
        action: async (repository) => {
            const url = repository.GitHubApp.getAuthorizeAppUrl(window.location.href);
            window.location.replace(url);
        },
    });
    const { execute: disconnectUser } = useMutation({
        name: "Disconnect GitHub user",
        action: async (repository) => {
            await repository.GitHubApp.clearUser();
        },
        afterComplete: async () => onUserUpdated(),
    });
    const styles = {
        cardWrapper: css({
            padding: space[16],
            borderRadius: borderRadius.medium,
            border: `${borderWidth[1]} solid ${themeTokens.color.border.primary}`,
            backgroundColor: themeTokens.color.card.background.secondary.default,
            display: "flex",
            alignItems: "center",
            gap: space[12],
        }),
        name: css({
            font: text.body.bold.large,
        }),
        login: css({
            font: text.body.regular.medium,
            color: themeTokens.color.text.primary,
        }),
        actions: css({
            marginLeft: "auto",
        }),
    };
    if (!authorizationStatus.IsAuthorized) {
        return (<div className={styles.cardWrapper}>
                <GithubIcon size={36}/>
                <div>Connect your GitHub account to Octopus</div>
                <div className={styles.actions}>
                    <Button onClick={() => reauthorizeUser()} label="Authorize" importance="primary"/>
                </div>
            </div>);
    }
    // When removing the feature toggle, we'll always be using the GitHubAppAuthorizationStatusWithUserDetails type, so we won't need the
    // UserDetails check anymore. We technically don't need it here because if the toggle is on then we're always going to have UserDetails,
    // and we exist early above if the toggle is on, but nice to handle it anyway
    if (!("UserDetails" in authorizationStatus)) {
        return null;
    }
    const userDetails = authorizationStatus.UserDetails;
    const overflowMenuItems: MenuItem[] = [OverflowMenuItems.item("Refresh", () => refreshUser()), OverflowMenuItems.item("Reauthorize", () => reauthorizeUser()), OverflowMenuItems.item("Disconnect", () => disconnectUser())];
    return (<div className={styles.cardWrapper}>
            <GitHubUserAvatar user={userDetails}/>
            <div>
                <div className={styles.name}>{userDetails.Name}</div>
                <div className={styles.login}>
                    {userDetails.Login} {userDetails.PrimaryEmail && `(${userDetails.PrimaryEmail})`}
                </div>
            </div>
            <div className={styles.actions}>
                <OverflowMenu menuItems={overflowMenuItems}/>
            </div>
        </div>);
}
